import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, Target } from "lucide-react";
import imageCompression from "browser-image-compression";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

function QuickCompress500({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("jpeg");
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Helper function to update SEO meta tags
  const updateSEOTags = (seo, fallbackTitle) => {
    document.title = seo?.meta_title || fallbackTitle || 'Quick Compress to 500KB - KayTool';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seo?.meta_description || 'Quickly compress images to 500KB with our free online tool.');
    
    if (seo?.meta_keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', seo.meta_keywords);
    }
  };

  // Fetch CMS content for quick-500kb page
  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/quick-500kb`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          updateSEOTags(data.seo, data.title);
        }
      } catch (error) {
        console.log('No CMS content for quick-500kb page');
        updateSEOTags(null, 'Quick Compress to 500KB - KayTool');
      }
    };
    
    if (!embedded) {
      fetchPageContent();
    }
  }, [embedded]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith("image/"));
    if (files.length > 0) {
      processMultipleImages(files);
    } else {
      toast.error("Please drop valid image files");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processMultipleImages(files);
    }
    e.target.value = '';
  };

  const handleAddMoreImages = () => {
    addMoreInputRef.current?.click();
  };

  // Handle paste event for clipboard images
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) {
          const extension = file.type.split('/')[1] || 'png';
          const namedFile = new File([file], `pasted-image-${Date.now()}.${extension}`, { type: file.type });
          imageFiles.push(namedFile);
        }
      }
    }
    
    if (imageFiles.length > 0) {
      e.preventDefault();
      processMultipleImages(imageFiles);
      toast.success(`${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} pasted!`);
    }
  };

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, []);

  // Helper function to convert HEIC to JPEG
  const convertHeicToJpeg = async (file) => {
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.heic') || fileName.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif') {
      try {
        toast.info('Converting HEIC format...', { duration: 2000 });
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.92
        });
        // heic2any may return array for multi-image HEIC
        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        return new File([blob], newName, { type: 'image/jpeg' });
      } catch (error) {
        console.error('HEIC conversion error:', error);
        toast.error('Failed to convert HEIC image');
        throw error;
      }
    }
    return file;
  };

  const processMultipleImages = async (files) => {
    setIsProcessing(true);
    setCompressionProgress(0);
    
    const processedImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const result = await processAndCompressTo500KB(file);
      processedImages.push(result);
      setCompressionProgress(Math.round(((i + 1) / files.length) * 100));
    }
    
    setImages(prevImages => [...prevImages, ...processedImages]);
    setIsProcessing(false);
    setCompressionProgress(0);
    toast.success(`${files.length} image${files.length > 1 ? 's' : ''} compressed to 500KB!`);
  };

  const processAndCompressTo500KB = async (file) => {
    // Track original format before conversion
    const originalFileName = file.name.toLowerCase();
    const wasHeic = originalFileName.endsWith('.heic') || originalFileName.endsWith('.heif') || 
                    file.type === 'image/heic' || file.type === 'image/heif';
    const originalFormat = wasHeic ? 'HEIC' : file.type.split('/')[1]?.toUpperCase() || 'Unknown';
    
    // Convert HEIC to JPEG first if needed
    const processableFile = await convertHeicToJpeg(file);
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const img = new Image();
        img.onload = async () => {
          const imageData = {
            id: Math.random().toString(36).substr(2, 9),
            name: processableFile.name,
            originalSrc: e.target.result,
            originalSize: file.size, // Keep original file size for stats
            originalWidth: img.width,
            originalHeight: img.height,
            originalFormat: originalFormat, // Track original format
            wasConverted: wasHeic, // Flag if HEIC was converted
            file: processableFile,
            compressed: null
          };
          
          // Compress to 500KB
          const compressed = await compressTo500KB(processableFile);
          imageData.compressed = compressed;
          
          resolve(imageData);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(processableFile);
    });
  };

  const compressTo500KB = async (file, format = outputFormat) => {
    try {
      const targetSizeKB = 500;
      const targetSizeMB = targetSizeKB / 1024; // 500KB in MB
      const fileSizeMB = file.size / 1024 / 1024;
      
      // Determine file type based on format
      let fileType = "image/jpeg";
      if (format === "png") {
        fileType = "image/png";
      } else if (format === "webp") {
        fileType = "image/webp";
      }
      
      // If file is already smaller than target, minimal processing
      if (fileSizeMB <= targetSizeMB) {
        const options = {
          maxSizeMB: fileSizeMB,
          useWebWorker: true,
          initialQuality: 0.9,
          fileType: fileType,
          maxIteration: 5,
          preserveExif: false,
        };
        
        const compressedFile = await imageCompression(file, options);
        const url = URL.createObjectURL(compressedFile);
        
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            resolve({
              src: url,
              blob: compressedFile,
              size: compressedFile.size,
              width: img.width,
              height: img.height,
              format: format
            });
          };
          img.src = url;
        });
      }
      
      // Smart compression for 500KB target (iloveimg-style)
      // Key: resize large images to achieve target size while maintaining quality
      const options = {
        maxSizeMB: targetSizeMB,
        // Auto-resize to web dimensions for aggressive compression
        maxWidthOrHeight: 1280,
        useWebWorker: true,
        initialQuality: 0.8,
        fileType: fileType,
        maxIteration: 25,
        preserveExif: false,
        alwaysKeepResolution: false,
      };
      
      const compressedFile = await imageCompression(file, options);
      const url = URL.createObjectURL(compressedFile);
      
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            src: url,
            blob: compressedFile,
            size: compressedFile.size,
            width: img.width,
            height: img.height,
            format: format
          });
        };
        img.src = url;
      });
    } catch (error) {
      console.error("Compression error:", error);
      return null;
    }
  };

  // Recompress when format changes
  useEffect(() => {
    if (images.length > 0 && !isProcessing) {
      recompressAll();
    }
  }, [outputFormat]);

  const recompressAll = async () => {
    if (images.length === 0) return;
    
    setIsProcessing(true);
    setCompressionProgress(0);
    
    const updatedImages = [];
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const compressed = await compressTo500KB(img.file, outputFormat);
      updatedImages.push({ ...img, compressed });
      setCompressionProgress(Math.round(((i + 1) / images.length) * 100));
    }
    
    setImages(updatedImages);
    setIsProcessing(false);
    setCompressionProgress(0);
    toast.success(`Converted to ${outputFormat.toUpperCase()}!`);
  };

  const getFileExtension = () => {
    if (outputFormat === "png") return "png";
    if (outputFormat === "webp") return "webp";
    return "jpg";
  };

  const handleDownloadSingle = (image) => {
    if (!image.compressed) return;
    
    const link = document.createElement("a");
    link.href = image.compressed.src;
    const nameWithoutExt = image.name.replace(/\.[^/.]+$/, "");
    link.download = `${nameWithoutExt}_50kb.${getFileExtension()}`;
    link.click();
    toast.success("Download started!");
  };

  const handleDownloadAll = async () => {
    if (images.length === 0) return;
    
    const zip = new JSZip();
    const ext = getFileExtension();
    
    for (const image of images) {
      if (image.compressed) {
        const nameWithoutExt = image.name.replace(/\.[^/.]+$/, "");
        zip.file(`${nameWithoutExt}_50kb.${ext}`, image.compressed.blob);
      }
    }
    
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `compressed_50kb_${Date.now()}.zip`);
    toast.success("All images downloaded!");
  };

  const handleRemoveImage = (imageId) => {
    setImages(images.filter(img => img.id !== imageId));
    toast.success("Image removed");
  };

  const handleClearAll = () => {
    setImages([]);
    toast.info("All images cleared");
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const calculateTotalStats = () => {
    let totalOriginal = 0;
    let totalCompressed = 0;
    
    images.forEach(img => {
      totalOriginal += img.originalSize;
      if (img.compressed) {
        totalCompressed += img.compressed.size;
      }
    });
    
    const savings = totalOriginal - totalCompressed;
    const percentage = totalOriginal > 0 ? Math.round((savings / totalOriginal) * 100) : 0;
    
    return { totalOriginal, totalCompressed, savings, percentage };
  };

  const stats = calculateTotalStats();

  // If embedded, render a simplified version
  if (embedded) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
        <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
        
        {/* Embedded Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-900 dark:text-white">Quick 500KB</span>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setDarkMode(!darkMode)}
            className="rounded-full w-8 h-8 border-slate-200 dark:border-slate-600"
          >
            {darkMode ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-slate-600" />}
          </Button>
        </div>

        {/* Upload Zone */}
        {images.length === 0 ? (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`h-48 border-2 border-dashed rounded-lg cursor-pointer ${
              isDragging ? "border-purple-500 bg-orange-50 dark:bg-purple-900/20" : "border-slate-300 dark:border-slate-600"
            } bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 flex flex-col items-center justify-center gap-2 transition-colors`}
          >
            <Upload className="w-8 h-8 text-purple-500" />
            <p className="text-sm text-slate-600 dark:text-slate-400">Drop images to compress to 500KB</p>
            <Badge className="bg-purple-500 text-white text-xs">Auto-compress</Badge>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Format selector */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Output format:</span>
              <Select value={outputFormat} onValueChange={setOutputFormat} disabled={isProcessing}>
                <SelectTrigger className="w-28 bg-white dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800">
                  <SelectItem value="jpeg">JPEG</SelectItem>
                  <SelectItem value="png">PNG</SelectItem>
                  <SelectItem value="webp">WebP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
                <p className="text-slate-500 dark:text-slate-400">Original</p>
                <p className="font-semibold text-slate-900 dark:text-white">{formatBytes(stats.totalOriginal)}</p>
              </div>
              <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
                <p className="text-slate-500 dark:text-slate-400">Compressed</p>
                <p className="font-semibold text-purple-500">{formatBytes(stats.totalCompressed)}</p>
              </div>
              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded">
                <p className="text-emerald-600 dark:text-emerald-400">Saved</p>
                <p className="font-semibold text-emerald-600 dark:text-emerald-400">{stats.percentage}%</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => images.length === 1 ? handleDownloadSingle(images[0]) : handleDownloadAll()}
                className="bg-purple-500 hover:bg-purple-600"
                size="sm"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
              <Button onClick={handleAddMoreImages} variant="outline" size="sm">
                <Upload className="w-4 h-4 mr-1" />
                Add More
              </Button>
              <Button onClick={handleClearAll} variant="outline" size="sm" className="text-red-600 border-red-200">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>

            {/* Images preview */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {images.slice(0, 6).map((image) => (
                <div key={image.id} className="relative aspect-square bg-slate-100 dark:bg-slate-700 rounded overflow-hidden">
                  <img src={image.compressed?.src || image.originalSrc} alt={image.name} className="w-full h-full object-cover" />
                  {image.wasConverted && (
                    <span className="absolute top-1 left-1 text-[9px] bg-purple-500 text-white px-1 rounded">
                      HEIC
                    </span>
                  )}
                  {image.compressed && (
                    <span className="absolute bottom-1 right-1 text-xs bg-emerald-500 text-white px-1 rounded">
                      {formatBytes(image.compressed.size)}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {images.length > 6 && (
              <p className="text-sm text-slate-500 text-center">+{images.length - 6} more images</p>
            )}
          </div>
        )}

        {/* Hidden inputs */}
        <input ref={addMoreInputRef} type="file" accept="image/*,.heic,.heif" multiple onChange={handleFileSelect} className="hidden" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 grid-pattern transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50 transition-colors duration-300">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <a href="https://kaytool.com" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Quick 500KB
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">by KayTool</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <ToolsNav />
              <Badge className="hidden md:flex items-center gap-1 px-3 py-1 bg-purple-500 text-white">
                <Zap className="w-3 h-3" />
                Instant 500KB Compression
              </Badge>
              <Button
                data-testid="dark-mode-toggle"
                variant="outline"
                size="icon"
                onClick={toggleDarkMode}
                className="rounded-full w-10 h-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors duration-300"
              >
                {darkMode ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      {/* Progress Bar */}
      {isProcessing && (
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-16 z-40 transition-colors duration-300">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <ToolsNav />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Compressing to 500KB...
                  </span>
                  {compressionProgress > 0 && (
                    <span className="text-sm font-medium text-purple-600 dark:text-purple-400">{compressionProgress}%</span>
                  )}
                </div>
                <Progress value={compressionProgress || 0} className="h-2" />
              </div>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-500 border-t-transparent"></div>
            </div>
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 py-8 md:py-12">

        {/* Upload Zone */}
        {images.length === 0 && (
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {pageContent?.title ? pageContent.title : <>Compress to <span className="text-purple-500">500KB</span> Instantly</>}
              </h2>
              <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                {pageContent?.seo?.meta_description || "Drop any image and get it compressed to under 500KB automatically. Great balance of quality and file size."}
              </p>
            </div>

            <div
              data-testid="upload-zone"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`upload-zone h-80 border-4 border-dashed rounded-xl cursor-pointer ${
                isDragging ? "drag-over border-purple-500" : "border-slate-300 dark:border-slate-600"
              } bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 flex flex-col items-center justify-center gap-4 transition-colors duration-300`}
            >
              <div className="w-20 h-20 rounded-full bg-orange-50 dark:bg-purple-900/30 flex items-center justify-center">
                <Upload className="w-10 h-10 text-purple-500 dark:text-purple-400" />
              </div>
              <div className="text-center">
                <p className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  Drop, paste, or click to upload
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Ctrl+V to paste from clipboard • Multiple files supported</p>
              </div>
              <Badge className="bg-purple-500 text-white text-xs">Auto-compress to 500KB</Badge>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4 text-center">
                  <Zap className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-slate-900 dark:text-white">Instant</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">No settings needed</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4 text-center">
                  <Target className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-slate-900 dark:text-white">500KB Target</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Perfect for emails</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4 text-center">
                  <Info className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-slate-900 dark:text-white">100% Private</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Browser-only processing</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Results */}
        {images.length > 0 && (
          <div className="space-y-6">
            {/* Format Selector */}
            <Card className="border-orange-200 dark:border-purple-800 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-purple-900/20 dark:to-amber-900/20">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">Output Format</h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Choose your preferred download format</p>
                  </div>
                  <Select value={outputFormat} onValueChange={setOutputFormat} disabled={isProcessing}>
                    <SelectTrigger className="w-40 bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                      <SelectItem value="jpeg">JPEG/JPG</SelectItem>
                      <SelectItem value="png">PNG</SelectItem>
                      <SelectItem value="webp">WebP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Stats & Actions */}
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1 w-full">
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Original</p>
                    <p className="text-xl font-bold text-slate-900 dark:text-white">{formatBytes(stats.totalOriginal)}</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Compressed</p>
                    <p className="text-xl font-bold text-purple-500">{formatBytes(stats.totalCompressed)}</p>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1">Saved</p>
                    <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{stats.percentage}%</p>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-wrap gap-2 w-full lg:w-auto">
                <Button
                  data-testid="download-all-button"
                  onClick={() => images.length === 1 ? handleDownloadSingle(images[0]) : handleDownloadAll()}
                  disabled={isProcessing}
                  className="rounded-full px-6 py-6 shadow-lg shadow-purple-500/20 font-bold bg-purple-500 hover:bg-purple-600 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {images.length === 1 ? 'Download' : `Download All (${images.length})`}
                </Button>
                <Button
                  data-testid="clear-all-button"
                  onClick={handleClearAll}
                  disabled={isProcessing}
                  variant="outline"
                  className="rounded-full px-6 py-6 border-2 border-red-500 dark:border-red-600 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                >
                  <X className="w-4 h-4 mr-2" />
                  {images.length === 1 ? 'Clear' : 'Clear All'}
                </Button>
                <Button
                  onClick={handleAddMoreImages}
                  variant="outline"
                  className="rounded-full px-6 py-6 border-2 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Add More
                </Button>
              </div>
            </div>

            {/* Ad Placement - Above Results (336×280 - Best Money Position) */}
            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-6" />

            {/* Images Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <Card key={image.id} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 shadow-sm overflow-hidden">
                  <div className="relative aspect-video bg-slate-100 dark:bg-slate-700">
                    <img 
                      src={image.compressed?.src || image.originalSrc} 
                      alt={image.name}
                      className="w-full h-full object-cover"
                    />
                    {image.wasConverted && (
                      <Badge className="absolute top-2 left-2 bg-purple-500 text-white text-[10px]">
                        HEIC → JPG
                      </Badge>
                    )}
                    <Badge className="absolute top-2 right-2 bg-emerald-500 text-white text-xs">
                      {image.compressed ? formatBytes(image.compressed.size) : 'Processing...'}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate mb-2">{image.name}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-3">
                      <span>{formatBytes(image.originalSize)} → {image.compressed ? formatBytes(image.compressed.size) : '...'}</span>
                      {image.compressed && (
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                          -{Math.round(((image.originalSize - image.compressed.size) / image.originalSize) * 100)}%
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDownloadSingle(image)}
                        disabled={!image.compressed}
                        className="flex-1 rounded-full bg-purple-500 hover:bg-purple-600 text-white"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveImage(image.id)}
                        className="rounded-full border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

          </div>
        )}

        {/* Ad Placement - Before Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content - After Tool (Same style as homepage) */}
      {pageContent?.components?.length > 0 && (
        <section className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 transition-colors duration-300">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <div className="max-w-4xl mx-auto">
              <RenderCMSContent components={pageContent.components} />
            </div>
          </div>
        </section>
      )}

      {/* Hidden file input for adding more images */}
      <input
        ref={addMoreInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />


      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md mt-16 transition-colors duration-300">
        <div className="container mx-auto px-4 py-6 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            All processing happens locally in your browser. Your images are never uploaded to any server.
          </p>
          <a href="/" className="text-sm text-purple-500 hover:text-purple-600 mt-2 inline-block">
            Need more options? Try the full compressor →
          </a>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}

export default QuickCompress500;
